import { Button } from '@hologram-dimension/button';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import SimAddonsPanel from 'components/SimAddonsPanel/SimAddonsPanel';
import SimPlanPanel from 'components/SimPlanPanel/SimPlanPanel';
import useIsDeactivatedOldSim from 'hooks/SimDetails/useIsDeactivatedOldSim';
import { type FC } from 'react';
import type { OnSimsModified, OpenModalCallback } from 'types/common';
import useSimUrl from 'utils/hooks/useSimUrl';
import styles from './SimPricingView.module.css';

interface PricingViewProps {
  hologramSimId?: HologramSimId;
  orgId?: OrgId;
  simDetailBaseUrl: string;
  dataPlanUrl: string;
  handleModalOpen: OpenModalCallback;
  onSimsModified: OnSimsModified;
  openPurchasePhoneNumberModal: () => void;
  openRemovePhoneNumberModal: () => void;
}
const SimPricingView: FC<PricingViewProps> = ({
  hologramSimId,
  orgId,
  simDetailBaseUrl,
  dataPlanUrl,
  handleModalOpen,
  onSimsModified,
  openPurchasePhoneNumberModal,
  openRemovePhoneNumberModal,
}) => {
  const { isDeactivatedOldSim, isLoading, error } = useIsDeactivatedOldSim({ hologramSimId });

  const url = useSimUrl({ simDetailBaseUrl, orgId, simId: hologramSimId });

  if (error) {
    return (
      <PanelNotification
        className={styles.error}
        variant="error"
        label="We ran into an issue loading the SIM pricing details"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  }

  if (isLoading) {
    return <PanelNotification className={styles.loader}>Loading SIM...</PanelNotification>;
  }

  if (isDeactivatedOldSim) {
    return (
      <PanelNotification
        className={styles.deactivated}
        label="SIM deactivated"
        icon="StatusDeactivated"
        actions={
          <Button variant="tertiary" iconEnd="ArrowEastShort" to={`${url}/usage`}>
            View historical data usage
          </Button>
        }
      >
        This SIM has been permanently deactivated from use on the Hologram network. It can no longer
        use data and cannot be reactivated. The last known information for this SIM is displayed if
        available.
      </PanelNotification>
    );
  }

  return (
    <>
      <SimPlanPanel
        hologramSimId={hologramSimId}
        orgId={orgId}
        handleModalOpen={handleModalOpen}
        onSimsModified={onSimsModified}
        simDetailBaseUrl={simDetailBaseUrl}
        dataPlanUrl={dataPlanUrl}
      />
      <SimAddonsPanel
        hologramSimId={hologramSimId}
        orgId={orgId}
        openPurchasePhoneNumberModal={openPurchasePhoneNumberModal}
        openRemovePhoneNumberModal={openRemovePhoneNumberModal}
      />
    </>
  );
};

export default SimPricingView;
